import { useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Lock, Mail, ShieldCheck, Wifi, Zap } from "lucide-react";
import logo from "@/assets/logo-contato.svg";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Acesso administrativo — Contato Internet" },
      { name: "description", content: "Área restrita para gestão do site da Contato Internet." },
      { property: "og:title", content: "Acesso administrativo — Contato Internet" },
      { property: "og:description", content: "Área restrita para gestão do site." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthPage,
});

const perks = [
  { icon: Zap, title: "Planos e preços", text: "Atualize velocidades e valores em segundos." },
  { icon: Wifi, title: "Banners e imagens", text: "Troque o banner principal e todas as fotos." },
  { icon: ShieldCheck, title: "Acesso seguro", text: "Somente administradores autorizados." },
];

function AuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      toast.success("Bem-vindo!");
      navigate({ to: "/admin" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível entrar");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-brand-deep text-brand-deep-foreground">
      {/* Aura de fundo */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 -top-40 size-[36rem] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle, var(--brand-sky-deep), transparent 65%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-52 -right-32 size-[34rem] rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(circle, var(--brand-lime), transparent 65%)" }}
      />

      <div className="relative mx-auto grid min-h-screen w-full max-w-6xl items-center gap-10 px-5 py-10 lg:grid-cols-2 lg:py-16">
        {/* Coluna institucional */}
        <div className="hidden lg:block">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-deep-foreground/70 transition hover:text-brand-yellow"
          >
            <ArrowLeft className="size-4" /> Voltar ao site
          </Link>

          <div className="mt-10 flex w-fit rounded-2xl bg-background px-5 py-3">
            <img src={logo} alt="Contato Internet" className="h-9 w-auto" />
          </div>

          <h2 className="mt-8 max-w-md text-4xl font-extrabold uppercase leading-[1.05] tracking-tight">
            Painel de <span className="text-brand-lime">gestão</span> do site Contato
          </h2>
          <p className="mt-4 max-w-md text-sm text-brand-deep-foreground/70">
            Controle total do conteúdo: planos de internet, telefonia, hospedagem, banners,
            parceiros e imagens — tudo em um só lugar.
          </p>

          <ul className="mt-10 space-y-4">
            {perks.map((p) => (
              <li key={p.title} className="flex items-start gap-4">
                <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-brand-yellow text-brand-yellow-foreground">
                  <p.icon className="size-5" />
                </span>
                <div>
                  <p className="text-sm font-bold uppercase tracking-wide">{p.title}</p>
                  <p className="text-sm text-brand-deep-foreground/65">{p.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Card de login */}
        <div className="mx-auto w-full max-w-md">
          <Link
            to="/"
            className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-deep-foreground/70 transition hover:text-brand-yellow lg:hidden"
          >
            <ArrowLeft className="size-4" /> Voltar ao site
          </Link>

          <div className="rounded-[2rem] border border-brand-deep-foreground/10 bg-background/95 p-8 text-foreground shadow-2xl backdrop-blur-xl sm:p-10">
            <div className="flex items-center justify-between gap-4 lg:hidden">
              <img src={logo} alt="Contato Internet" className="h-8 w-auto" />
            </div>

            <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-lime/25 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-brand-deep lg:mt-0">
              <Lock className="size-3" /> Área restrita
            </span>

            <h1 className="mt-4 text-3xl font-extrabold uppercase leading-tight tracking-tight text-brand-deep">
              Entrar
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Use suas credenciais de administrador para gerenciar o site.
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wide">
                  E-mail
                </Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    required
                    placeholder="voce@contato.net"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    className="h-12 rounded-2xl border-border/70 bg-muted/40 pl-11 text-base focus-visible:ring-brand-sky-deep"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wide">
                  Senha
                </Label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    required
                    minLength={6}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="h-12 rounded-2xl border-border/70 bg-muted/40 pl-11 text-base focus-visible:ring-brand-sky-deep"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="h-12 w-full rounded-full bg-brand-lime text-base font-extrabold uppercase tracking-wide text-brand-deep transition hover:brightness-95"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" /> Aguarde...
                  </>
                ) : (
                  "Entrar no painel"
                )}
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-xs text-brand-deep-foreground/50">
            Contato Internet — acesso monitorado e exclusivo para administradores.
          </p>
        </div>
      </div>
    </div>
  );
}
